import { CameraOutlinedIcon } from '@village/icons';
import { Button, Divider, Link } from '@village/ui';
import { DepartmentField, IsExistingPatientField, ReasonField, ReasonOption } from 'components/fields';
import { MarketField } from 'components/fields/market';
import { PageHeader } from 'components/page-header';
import { VisitTypeBanner } from 'components/visit-type-banner';
import {
    useAutocompleteScrollHandler,
    useBooking,
    useCaptureCountlyEvent,
    useDepartments,
    useMarkets,
    useReasons,
    useScrollToTop,
    useSyncBookingWithUrl,
} from 'hooks';
import { addGoogleTagManagerEvent } from 'modules/google-tag-manager';
import { useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Market } from 'types';

import * as Styled from './styles';

const SelectAppointmentDetailsPage = () => {
    useSyncBookingWithUrl();
    useScrollToTop();

    const departmentFieldRef = useRef(null);
    const navigate = useNavigate();
    const { search } = useLocation();
    const { addCountlyEvent } = useCaptureCountlyEvent();
    const { setBookingField, booking } = useBooking();
    const { data: markets, isFetching: isMarketsFetching } = useMarkets();
    const { data: reasons, isFetching: isReasonsFetching } = useReasons(booking);
    const { data: departments, isFetching: isDepartmentsLoading } = useDepartments(booking);
    const handleAutocompleteScrollOnMobile = useAutocompleteScrollHandler();

    const handleReasonChange = useCallback(
        (option: ReasonOption | null) => {
            if (!booking.reason) {
                addCountlyEvent({ key: 'selectReason' });
            }

            const reason = reasons?.find(({ id }) => option?.reasonId === id);
            setBookingField({ reason });
        },
        [booking.reason, reasons, setBookingField, addCountlyEvent]
    );

    const handleFindAppointments = useCallback(() => {
        addCountlyEvent({ key: 'step2.clickFindAppointments' });
        addGoogleTagManagerEvent('find-appointments', booking);

        navigate({
            pathname: '/select-appointment',
            search,
        });
    }, [navigate, addCountlyEvent, search, booking]);

    const handleOnMarketChange = useCallback(
        (market: Market | undefined) => {
            if (market !== booking.market) {
                setBookingField({ market, department: undefined, reason: undefined, provider: undefined });
            }
        },
        [setBookingField, booking.market]
    );

    return (
        <Styled.Wrapper>
            <Styled.FormContainer>
                <PageHeader />
                <MarketField
                    data-testid="select-market"
                    label="Select region"
                    placeholder="Please select"
                    id="select-market"
                    markets={markets}
                    isClearable={true}
                    isLoading={isMarketsFetching}
                    onChange={handleOnMarketChange}
                />
                <Divider />
                <IsExistingPatientField />
                <div>
                    <DepartmentField
                        ref={departmentFieldRef}
                        label="Select preferred location"
                        placeholder="Please select"
                        id="select-department"
                        departments={departments}
                        isClearable={true}
                        isLoading={isDepartmentsLoading}
                        onFocus={handleAutocompleteScrollOnMobile(departmentFieldRef)}
                    />
                    <Styled.DepartmentHelperText type="body2">
                        Not sure which location?{' '}
                        <Link
                            href="https://www.villagemedical.com/locator"
                            rel="noopener noreferrer"
                            data-testid="map-link"
                            target="_blank"
                        >
                            View map
                        </Link>{' '}
                        or{' '}
                        <Link
                            href="https://www.villagemedical.com/our-providers"
                            rel="noopener noreferrer"
                            data-testid="provider-link"
                            target="_blank"
                        >
                            find a provider
                        </Link>
                    </Styled.DepartmentHelperText>
                </div>
                <ReasonField
                    label="Reason for visit"
                    id="select-reason"
                    onChange={handleReasonChange}
                    reasons={reasons}
                    isLoading={isReasonsFetching}
                />
                {booking.reason && !booking.reason.is_in_person ? (
                    <VisitTypeBanner
                        title="You’ve selected a video visit"
                        subtitle="Your provider will send you a link to access the appointment via email or text message shortly before the visit."
                        icon={CameraOutlinedIcon}
                    />
                ) : null}
                <Button
                    size="large"
                    onClick={handleFindAppointments}
                    disabled={!booking.reason}
                    data-testid="button-find-appointments"
                >
                    Find appointments
                </Button>
            </Styled.FormContainer>
        </Styled.Wrapper>
    );
};

export { SelectAppointmentDetailsPage };
